import { css } from '@styled-system/css';
import { stack } from '@styled-system/patterns';
import type { FC } from 'react';
import { useState } from 'react';

import type { AnalysisHeaderData } from '@/features/analysisPage/types';
import { Button } from '@/shared/components/ui/Button';
import { Logo } from '@/shared/components/ui/Logo';
import { Tag } from '@/shared/components/ui/Tag';
import { useIsMobile } from '@/shared/hooks/useIsMobile';
import { formatDate } from '@/shared/utils/format';

import { RenameAnalysisModal } from './RenameAnalysisModal';

interface AnalysisPageHeaderProps {
  analysis: AnalysisHeaderData;
  handleRename?: (newName: string) => Promise<void>;
  handleStop?: () => void;
  handleDelete?: () => void;
  isUpdating?: boolean;
  onBackToMenu?: () => void;
}

export const AnalysisPageHeader: FC<AnalysisPageHeaderProps> = ({
  analysis,
  handleRename,
  handleStop,
  handleDelete,
  isUpdating = false,
  onBackToMenu,
}) => {
  const isMobile = useIsMobile();
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);

  const containerStyles = stack({
    direction: 'column',
    gap: '0.5rem',
    w: '100%',
  });

  const backButtonContainerStyles = css({
    position: 'fixed',
    top: '0.75rem',
    left: '1rem',
    zIndex: 1,
  });

  const headerStyles = stack({
    direction: 'row',
    alignItems: 'flex-start',
    gap: '1rem',
    p: '1rem',
    bg: 'transparent',
    w: '100%',
  });

  const headerContentStyles = stack({
    direction: 'column',
    gap: '0.25rem',
  });

  const headerTopContentStyles = stack({
    direction: 'row',
    gap: '0.94rem',
    alignItems: 'center',
  });

  const imageStyles = css({
    w: '3.125rem', // 50px
    h: '3.125rem', // 50px
    borderRadius: 'sm', // 6px border radius selon design
    flexShrink: 0,
    objectFit: 'cover',
    bg: 'base.segBg', // Background par défaut si pas d'image
    fontSize: 0,
  });

  const titleLinkStyles = css({
    display: 'flex',
    alignItems: 'center',
    mt: '-0.25rem',
    gap: '0.5rem',
    fontSize: isMobile ? 'md' : 'xl',
    fontWeight: 600, // Manrope SemiBold
    fontFamily: 'Manrope, sans-serif',
    color: 'font.primary', // #e6e6e9
    textDecoration: 'underline',
    textDecorationColor: 'font.lowkey',
    transition: 'color 150ms ease',
    _hover: {
      color: 'font.secondary', // Slightly different on hover
    },
  });

  const externalLinkIconStyles = css({
    w: '0.9375rem', // 15px
    h: '0.9375rem', // 15px
    ml: '-0.4rem',
  });

  const statusTagStyles = css({
    flexShrink: 0,
  });

  const actionsContainerStyles = css({
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem', // 8px gap entre boutons
    flexShrink: 0,
  });

  const analysisDateStyles = css({
    fontSize: '2xs',
    color: 'font.lowkey',
    fontWeight: 600,
  });

  return (
    <div className={containerStyles}>
      {/* Back to Menu Button - Mobile only */}
      {isMobile && onBackToMenu && (
        <div className={backButtonContainerStyles}>
          <Button handleClick={onBackToMenu} icon="back" className={css({ bg: 'base.bg !important' })}>
            Retourner au menu
          </Button>
        </div>
      )}

      <div className={headerStyles}>
        {/* Product Image */}
        {analysis.imageUrl ? (
          <img src={analysis.imageUrl} alt={analysis.name} className={imageStyles} />
        ) : (
          <div className={imageStyles} />
        )}

        <div className={headerContentStyles}>
          <div className={headerTopContentStyles}>
            {/* Title with External Link */}
            <a href={analysis.pageUrl} target="_blank" rel="noopener noreferrer" className={titleLinkStyles}>
              <span>{analysis.name}</span>
              <Logo variant="external-link" color="font.primary" className={externalLinkIconStyles} />
            </a>

            {/* Status Tag */}
            <div className={statusTagStyles}>
              <Tag
                color={
                  analysis.status === 'starting'
                    ? 'accent.blue'
                    : analysis.status === 'active'
                      ? 'accent.orange'
                      : analysis.status === 'inactive'
                        ? 'font.lowkey'
                        : analysis.status === 'error'
                          ? 'accent.red'
                          : 'font.lowkey'
                }
              >
                {analysis.status === 'starting'
                  ? 'Démarrage...'
                  : analysis.status === 'active'
                    ? 'En cours'
                    : analysis.status === 'inactive'
                      ? 'Terminé'
                      : analysis.status === 'error'
                        ? 'Erreur'
                        : 'Supprimé'}
              </Tag>
            </div>
          </div>

          <p
            className={analysisDateStyles}
          >{`Créée le ${formatDate(analysis.createdAt)}${analysis.endedAt ? ` - Terminée le ${formatDate(analysis.endedAt)}` : ''}`}</p>
        </div>

        <div className={css({ flexGrow: 1 })} />

        {/* Action Buttons - Masqués pour les analyses publiques ou sur mobile */}
        {!(analysis.isPublic || isMobile) && (
          <div className={actionsContainerStyles}>
            {handleRename && (
              <Button handleClick={() => setIsRenameModalOpen(true)} icon="rename" disabled={isUpdating}>
                Renommer
              </Button>
            )}
            {handleStop && (analysis.status === 'starting' || analysis.status === 'active') && (
              <Button handleClick={handleStop} icon="stop" color="accent.red" disabled={isUpdating}>
                Arrêter
              </Button>
            )}
            {handleDelete && analysis.status === 'inactive' && (
              <Button handleClick={handleDelete} icon="trash" color="accent.red" disabled={isUpdating}>
                Supprimer
              </Button>
            )}
          </div>
        )}

        {/* Rename Modal - Masqué pour les analyses publiques */}
        {handleRename && !analysis.isPublic && (
          <RenameAnalysisModal
            isOpen={isRenameModalOpen}
            onClose={() => setIsRenameModalOpen(false)}
            currentName={analysis.name}
            onRename={handleRename}
            isRenaming={isUpdating}
          />
        )}
      </div>
    </div>
  );
};
